import { StandardProperties } from 'csstype';

export type UseAnimationCssProperties = StandardProperties & TransformProperties;

// This represents shorthand UseAnimationCssProperties props.
type TransformProperties = Partial<
  Record<
    | 'translateX'
    | 'translateY'
    | 'translateZ'
    | 'rotateX'
    | 'rotateY'
    | 'rotateZ'
    | 'skewX'
    | 'skewY',
    string
  > &
    Record<'scaleX' | 'scaleY' | 'scaleZ', string | number>
>;

/**
 * Converts shorthand translate props into transform string representation.
 *
 * @param translateX
 * @param translateY
 * @param translateZ
 * @returns Longhand transform string representation.
 */
function convertTranslateShortcuts(
  translateX: TransformProperties['translateX'],
  translateY: TransformProperties['translateY'],
  translateZ: TransformProperties['translateZ']
) {
  return `${translateX ? ` translateX(${translateX})` : ''}${
    translateY ? ` translateY(${translateY})` : ''
  }${translateZ ? ` translateZ(${translateZ})` : ''}`;
}

/**
 * Converts shorthand rotate props into transform string representation.
 *
 * @param rotateX
 * @param rotateY
 * @param rotateZ
 * @returns Longhand transform string representation.
 */
function convertRotateShortcuts(
  rotateX?: TransformProperties['rotateX'],
  rotateY?: TransformProperties['rotateY'],
  rotateZ?: TransformProperties['rotateZ']
) {
  return `${rotateX ? ` rotateX(${rotateX})` : ''}${rotateY ? ` rotateY(${rotateY})` : ''}${
    rotateZ ? ` rotateZ(${rotateZ})` : ''
  }`;
}

/**
 * Converts shorthand scale props into transform string representation.
 *
 * @param scaleX
 * @param scaleY
 * @param scaleZ
 * @returns Longhand transform string representation.
 */
function convertScaleShortcuts(
  scaleX: TransformProperties['scaleX'],
  scaleY: TransformProperties['scaleY'],
  scaleZ: TransformProperties['scaleZ']
) {
  return `${scaleX || scaleX === 0 ? ` scaleX(${scaleX})` : ''}${
    scaleY || scaleY === 0 ? ` scaleY(${scaleY})` : ''
  }${scaleZ || scaleZ === 0 ? ` scaleZ(${scaleZ})` : ''}`;
}

/**
 * Converts shorthand skew props into transform string representation.
 *
 * @param skewX
 * @param skewY
 * @returns Longhand transform string representation.
 */
function convertSkewShortcuts(
  skewX: TransformProperties['skewX'],
  skewY: TransformProperties['skewY']
) {
  return `${skewX ? ` skewX(${skewX})` : ''}${skewY ? ` skewY(${skewY})` : ''}`;
}

/**
 * Converts animation CSS properties into Regular camel cased CSS properties.
 *
 * @param useAnimationCssProperties This include transform shorthand props.
 * @returns Regular camel cased CSS properties.
 */
export function convertUseAnimationCssPropertiesToRegularCSS(
  useAnimationCssProperties?: UseAnimationCssProperties
) {
  if (!useAnimationCssProperties) {
    return {};
  }
  const {
    translateX,
    translateY,
    translateZ,
    rotateX,
    rotateY,
    rotateZ,
    scaleX,
    scaleY,
    scaleZ,
    skewX,
    skewY,
    ...otherCssProperties
  } = useAnimationCssProperties;
  const initialTransformValue = useAnimationCssProperties['transform'] || '';
  // Now we just add values pased in shortcuts to transform property, do we want to replace it instead?
  const finalTransformValue = `${initialTransformValue}${convertTranslateShortcuts(
    translateX,
    translateY,
    translateZ
  )}${convertRotateShortcuts(rotateX, rotateY, rotateZ)}${convertScaleShortcuts(
    scaleX,
    scaleY,
    scaleZ
  )}${convertSkewShortcuts(skewX, skewY)}`;
  return {
    ...otherCssProperties,
    ...(finalTransformValue && { transform: finalTransformValue })
  };
}

/**
 * Converts animation CSS properties into WAAPI keyframe properties.
 * Since keyframes have a difference between offset and cssOffset this collision is avoided here.
 *
 * @param useAnimationCssProperties This include transform shorthand props.
 * @returns WAAPI keyframe
 */
export function convertUseAnimationCssPropertiesToWAAPIKeyframe(
  useAnimationCssProperties?: UseAnimationCssProperties
) {
  if (!useAnimationCssProperties) {
    return {};
  }
  const { offset, ...otherCssProperties } =
    convertUseAnimationCssPropertiesToRegularCSS(useAnimationCssProperties);
  return {
    ...otherCssProperties,
    ...(offset && { cssOffset: offset })
  };
}

/**
 * Stops an animation.
 *
 * @param animation Animation that is going to be stopped.
 * @returns
 */
export function stopAnimation<E extends HTMLElement>(animation: Animation, node: E | null) {
  // TO DO: rollup-plugin-typescript seems to have an outdated Animation typing. That produces warnings while bulding.
  // Update picked type to get rid of this casting.
  // We have to check if the element is visible to commit styles
  isVisible(node) && (animation as any).commitStyles();
  animation.cancel();
}

/**
 * Check if an element is visible. Similar to jQuery :visible selector.
 *
 * @param element HTML element that is going to be checked for its visibility.
 * @returns
 */
function isVisible<E extends HTMLElement>(element: E | null): boolean {
  return !!(element?.offsetWidth || element?.offsetHeight || element?.getClientRects().length);
}
